import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { getCourseDetailByIdGuest } from '../../api/courses';
import { SEO } from '../../components/SEO';
import './CoursePlayer.scss';

const formatDuration = (val) => {
  if (!val && val !== 0) return '00:00';
  if (typeof val === 'string' && val.includes(':')) return val;
  const num = parseInt(val, 10);
  if (isNaN(num)) return val;
  const m = Math.floor(num / 60);
  const s = num % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

export const CourseDetailGuest = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [courseData, setCourseData] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [openModules, setOpenModules] = useState(['mod-1']);

  const handleLoginRedirect = () => {
    navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
  };

  const toggleModule = (modId) => {
    setOpenModules(prev =>
      prev.includes(modId) ? prev.filter(m => m !== modId) : [...prev, modId]
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getCourseDetailByIdGuest(id);
        let cData = res && res.data ? res.data : null;
        if (!cData && res && !res.status) cData = res;

        if (cData) {
          setCourseData(cData);
        } else {
          setCourseData({ title: 'Course Details', category_name: 'Education' });
        }

        // Build placeholder locked modules from lesson count if available
        const lessonCount = cData?.total_lessons || cData?.lessons_count || 0;
        if (lessonCount > 0) {
          const mod = {
            id: 'mod-1',
            title: 'Course Content',
            completedCount: 0,
            totalCount: lessonCount,
            lessons: Array.from({ length: lessonCount }, (_, i) => ({
              id: `guest-lesson-${i}`,
              num: `${i + 1}.`,
              title: `Lesson ${i + 1}`,
              duration: '—',
              status: 'locked',
            })),
          };
          setLessons([mod]);
          setOpenModules(['mod-1']);
        }
      } catch (err) {
        console.warn('Guest course detail fetch error:', err);
        setCourseData({ title: 'Course Details', category_name: 'Education' });
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  if (loading || !courseData) {
    return (
      <div className="course-player-page justify-content-center align-items-center bg-dark text-white d-flex">
        <div className="spinner-border text-yellow"></div>
      </div>
    );
  }

  const categoryName = courseData.category_name || 'Education';
  const courseTitle = courseData.title || 'Course Details';
  const coverImage =
    courseData.cover_image || courseData.coverImage || courseData.image ||
    'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&h=450&fit=crop';
  const coverVideo = courseData.coverVideo || courseData.cover_video;

  const totalLessons = lessons.reduce((acc, mod) => acc + mod.totalCount, 0);
  const calcDuration = courseData.duration || courseData.hours ||
    (totalLessons > 0 ? `${totalLessons * 5}m est.` : '—');

  return (
    <div className="course-player-page">
      <SEO title={courseTitle} />

      {/* Top Navigation */}
      <div className="player-top-nav">
        <div className="breadcrumbs">
          <Link to="/courses">Courses</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to="/courses">{categoryName}</Link>
          <i className="bi bi-chevron-right"></i>
          <span className="active">{courseTitle}</span>
        </div>
        <div className="top-actions">
          <button className="btn-nav-action" onClick={() => navigate('/courses')}>
            <i className="bi bi-box-arrow-right"></i> Exit Course
          </button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="player-main-content">
        <Row className="g-4 m-0">

          {/* Left Column - Player & Details */}
          <Col xl={8} lg={8} md={8} className="p-0 pe-md-3">

            {/* Video Container with Lock Overlay */}
            <div className="video-container" style={{ position: 'relative' }}>
              {/* Background image / cover video */}
              {coverVideo ? (
                (() => {
                  const isYouTube = coverVideo.includes('youtube.com') || coverVideo.includes('youtu.be');
                  if (isYouTube) {
                    const match = coverVideo.match(/(?:youtu\.be\/|v=)([^?&]+)/);
                    const embedUrl = match ? `https://www.youtube.com/embed/${match[1]}` : coverVideo;
                    return (
                      <iframe
                        src={embedUrl}
                        title="Course Preview"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                      />
                    );
                  }
                  return (
                    <video
                      src={coverVideo}
                      poster={coverImage}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      muted
                      playsInline
                    />
                  );
                })()
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  backgroundImage: `url("${coverImage}")`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                }} />
              )}

              {/* Lock Overlay */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(6,11,25,0.78)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: '1.25rem',
              }}>
                <div style={{
                  width: '72px', height: '72px', borderRadius: '50%',
                  background: 'rgba(79,70,229,0.15)', border: '2px solid #4f46e5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <i className="bi bi-lock-fill" style={{ fontSize: '2rem', color: '#818cf8' }}></i>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.35rem' }}>
                    Sign in to Watch
                  </p>
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                    Create a free account to access all lessons
                  </p>
                  <button
                    onClick={handleLoginRedirect}
                    style={{
                      background: '#4f46e5', color: 'white', border: 'none',
                      borderRadius: '8px', padding: '0.65rem 2.5rem',
                      fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
                    }}
                  >
                    <i className="bi bi-box-arrow-in-right me-2"></i>Login / Sign Up
                  </button>
                </div>
              </div>
            </div>

            {/* Lesson / Course Header */}
            <div className="lesson-header">
              <div className="title-area">
                <h6 style={{ color: '#818cf8', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.85rem' }}>
                  {courseTitle}
                </h6>
                <h2>Course Overview</h2>
                <div className="lesson-meta">
                  <div className="meta-item"><i className="bi bi-clock"></i> {calcDuration}</div>
                  <div className="meta-item"><i className="bi bi-camera-video"></i> {totalLessons > 0 ? `${totalLessons} Lessons` : 'Video Lessons'}</div>
                  <div className="meta-item"><i className="bi bi-reception-4"></i> {courseData?.level || 'Beginner'}</div>
                </div>
              </div>
              <div className="lesson-actions">
                <button
                  className="btn-action btn-mark-done"
                  onClick={handleLoginRedirect}
                >
                  <i className="bi bi-person-plus-fill"></i> Enroll Now
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="custom-tabs-container">
              <div className="custom-tabs">
                <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                <button className={`tab-btn ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')}>Resources</button>
                <button className={`tab-btn ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => setActiveTab('qa')}>Reviews</button>
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <Row className="g-4 m-0">
                <Col md={12} className="p-0">
                  <div className="info-card">
                    <h5>About This Course</h5>
                    <div className="text-white-50" style={{ whiteSpace: 'pre-wrap' }}>
                      {courseData?.description || 'Explore this amazing course on Yoospeak! Log in to see full course details, access all lessons, and track your progress.'}
                    </div>

                    {/* What you'll get CTA panel */}
                    <div style={{
                      marginTop: '1.5rem', padding: '1.25rem',
                      background: 'rgba(79,70,229,0.08)', borderRadius: '10px',
                      border: '1px solid rgba(79,70,229,0.25)',
                    }}>
                      <h6 style={{ color: '#818cf8', marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 700 }}>
                        <i className="bi bi-stars me-2"></i>What you'll get with an account
                      </h6>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        {[
                          ['bi-camera-video-fill', 'Access to all video lessons'],
                          ['bi-file-earmark-text-fill', 'Downloadable course resources'],
                          ['bi-check2-circle', 'Track your progress & mark lessons complete'],
                          ['bi-trophy-fill', 'Earn a certificate upon completion'],
                          ['bi-phone-fill', 'Mobile & desktop access'],
                        ].map(([icon, text]) => (
                          <li key={icon} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem', color: '#d1d5db' }}>
                            <i className={`bi ${icon}`} style={{ color: '#818cf8', fontSize: '1rem' }}></i>
                            {text}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={handleLoginRedirect}
                        style={{
                          marginTop: '1.25rem', background: '#4f46e5', color: 'white',
                          border: 'none', borderRadius: '8px', padding: '0.6rem 1.75rem',
                          fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                        }}
                      >
                        <i className="bi bi-box-arrow-in-right me-2"></i>Login to Access Full Course
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {/* Resources Tab */}
            {activeTab === 'resources' && (
              <div className="info-card">
                <h5 className="mb-4">Course Resources</h5>
                <div style={{
                  textAlign: 'center', padding: '2.5rem 1rem',
                  background: 'rgba(79,70,229,0.06)', borderRadius: '10px',
                  border: '1px dashed rgba(79,70,229,0.3)',
                }}>
                  <i className="bi bi-lock-fill" style={{ fontSize: '2.5rem', color: '#4f46e5', opacity: 0.6, display: 'block', marginBottom: '1rem' }}></i>
                  <p style={{ color: '#9ca3af', marginBottom: '1rem', fontWeight: 500 }}>
                    Resources are only available to enrolled students.
                  </p>
                  <button
                    onClick={handleLoginRedirect}
                    style={{
                      background: '#4f46e5', color: 'white', border: 'none',
                      borderRadius: '8px', padding: '0.55rem 1.5rem',
                      fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                    }}
                  >
                    Login to Access Resources
                  </button>
                </div>
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'qa' && (
              <div className="info-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Reviews</h5>
                  <button
                    onClick={handleLoginRedirect}
                    style={{
                      backgroundColor: '#4f46e5', border: 'none', borderRadius: '6px',
                      padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600,
                      color: 'white', cursor: 'pointer',
                    }}
                  >
                    Login to Review
                  </button>
                </div>
                <div style={{
                  textAlign: 'center', padding: '2rem',
                  background: 'rgba(79,70,229,0.06)', borderRadius: '10px',
                  border: '1px dashed rgba(79,70,229,0.3)',
                }}>
                  <i className="bi bi-chat-square-dots" style={{ fontSize: '2.5rem', color: '#818cf8', opacity: 0.5, display: 'block', marginBottom: '0.75rem' }}></i>
                  <p className="text-white-50">Log in to read and leave reviews for this course.</p>
                </div>
              </div>
            )}

          </Col>

          {/* Right Column - Curriculum */}
          <Col xl={4} lg={4} md={4} className="p-0 ps-md-3">
            <div className="sidebar-container">

              {/* Progress-style card with enroll CTA */}
              <div className="progress-card">
                <div className="progress-header">
                  <span>Course Content</span>
                  <span style={{ color: '#9ca3af' }}>{totalLessons > 0 ? `${totalLessons} Lessons` : '—'}</span>
                </div>
                <div className="progress-bar-bg">
                  <div className="progress-bar-fill" style={{ width: '0%' }}></div>
                </div>
                <div className="progress-meta">
                  {totalLessons > 0 ? `${totalLessons} Lesson${totalLessons !== 1 ? 's' : ''}` : '—'} • {calcDuration}
                </div>
                <button
                  onClick={handleLoginRedirect}
                  style={{
                    marginTop: '1rem', width: '100%',
                    background: '#4f46e5', color: 'white',
                    border: 'none', borderRadius: '8px',
                    padding: '0.65rem 1rem', fontWeight: 700,
                    fontSize: '0.9rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '0.5rem',
                  }}
                >
                  <i className="bi bi-person-plus-fill"></i> Login to Enroll
                </button>
              </div>

              {/* Modules List — all lessons locked */}
              <div className="modules-list">
                {lessons.length > 0 ? (
                  lessons.map((mod) => {
                    const isOpen = openModules.includes(mod.id);
                    return (
                      <div className="module-card" key={mod.id}>
                        <div className="module-header" onClick={() => toggleModule(mod.id)}>
                          <div className="mod-title">{mod.title}</div>
                          <div className="mod-meta">
                            <span>{mod.totalCount} lessons</span>
                            <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
                          </div>
                        </div>
                        {isOpen && mod.lessons && (
                          <div className="module-lessons">
                            {mod.lessons.map((lesson) => (
                              <div
                                key={lesson.id}
                                className="lesson-item"
                                onClick={handleLoginRedirect}
                                style={{ cursor: 'pointer', opacity: 0.65 }}
                              >
                                <div className="lesson-title-area">
                                  <span className="lesson-num">{lesson.num}</span>
                                  <span className="lesson-title">{lesson.title}</span>
                                </div>
                                <div className="lesson-right">
                                  <span className="lesson-duration">{lesson.duration}</span>
                                  <i className="bi bi-lock lesson-status-icon"></i>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  /* Fallback curriculum teaser */
                  <div className="module-card">
                    <div className="module-header" onClick={() => toggleModule('mod-guest')}>
                      <div className="mod-title">Course Curriculum</div>
                      <div className="mod-meta">
                        <i className={`bi bi-chevron-${openModules.includes('mod-guest') ? 'up' : 'down'}`}></i>
                      </div>
                    </div>
                    {openModules.includes('mod-guest') && (
                      <div className="module-lessons">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <div
                            key={n}
                            className="lesson-item"
                            onClick={handleLoginRedirect}
                            style={{ cursor: 'pointer', opacity: 0.6 }}
                          >
                            <div className="lesson-title-area">
                              <span className="lesson-num">{n}.</span>
                              <span className="lesson-title">Lesson {n}</span>
                            </div>
                            <div className="lesson-right">
                              <i className="bi bi-lock lesson-status-icon"></i>
                            </div>
                          </div>
                        ))}
                        <div
                          className="lesson-item"
                          onClick={handleLoginRedirect}
                          style={{
                            cursor: 'pointer', justifyContent: 'center',
                            color: '#818cf8', fontSize: '0.8rem', fontWeight: 600,
                          }}
                        >
                          <i className="bi bi-plus-circle me-2"></i> Login to see all lessons
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </Col>
        </Row>
      </div>

      {/* Bottom Navigation Bar */}
      <div className="bottom-nav-container">
        <div className="nav-side">
          {/* empty — no previous lesson for guests */}
        </div>

        <div className="nav-center">
          <div className="progress-msg">Create an account to start learning today!</div>
          <button className="btn-next-main" onClick={handleLoginRedirect}>
            <i className="bi bi-box-arrow-in-right me-2"></i>
            Login / Sign Up
            <i className="bi bi-chevron-right ms-2 fs-5"></i>
          </button>
        </div>

        <div className="nav-side next">
          {/* empty — no next lesson for guests */}
        </div>
      </div>

    </div>
  );
};

